import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/card';
import Button from '../components/common/button';
import { Heart, Moon, Star, Leaf, ChevronUp, ChevronDown } from 'lucide-react';

const FoodRestrictionsIntro = ({ onStart }) => {
  const navigate = useNavigate();
  const [showDefinitions, setShowDefinitions] = useState(true);

  // State for selections (for demonstration - these would be managed based on actual requirements)
  const [healthRestrictions, setHealthRestrictions] = useState([]);
  const [beliefAccommodations, setBeliefAccommodations] = useState([]);
  const [lifestyleAccommodations, setLifestyleAccommodations] = useState([]);

  const healthOptions = ['Gluten', 'Dairy', 'Egg', 'Soy', 'Nut', 'Shellfish', 'Fish', 'Alcohol', 'None'];
  const beliefOptions = ['Kosher', 'Halal', 'None'];
  const lifestyleOptions = ['Vegetarian', 'Vegan', 'Pescatarian', 'None'];

  const toggleHealthOption = (option) => {
    if (option === 'None') {
      setHealthRestrictions(['None']);
    } else {
      setHealthRestrictions(prev => {
        const filtered = prev.filter(item => item !== 'None');
        if (prev.includes(option)) {
          return filtered.filter(item => item !== option);
        } else {
          return [...filtered, option];
        }
      });
    }
  };

  const toggleBeliefOption = (option) => {
    if (option === 'None') {
      setBeliefAccommodations(['None']);
    } else {
      setBeliefAccommodations(prev => {
        const filtered = prev.filter(item => item !== 'None');
        if (prev.includes(option)) {
          return filtered.filter(item => item !== option);
        } else {
          return [...filtered, option];
        }
      });
    }
  };

  const toggleLifestyleOption = (option) => {
    if (option === 'None') {
      setLifestyleAccommodations(['None']);
    } else {
      setLifestyleAccommodations(prev => {
        const filtered = prev.filter(item => item !== 'None');
        if (prev.includes(option)) {
          return filtered.filter(item => item !== option);
        } else {
          return [...filtered, option];
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#fcf7ec] py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Main Card */}
        <Card className="bg-white shadow-lg rounded-lg p-6 sm:p-8 lg:p-10">
          {/* Chapter Intro Label */}
          <div className="mb-4">
            <span className="text-xs sm:text-sm bg-slate-200 py-1 px-4 rounded-full font-medium text-[#777777] tracking-wide">
              Chapter intro
            </span>
          </div>

          {/* Main Heading */}
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#333333] mb-3">
            What 'Restrictions' means here
          </h2>

          {/* Subheading */}
          <p className="text-sm sm:text-lg text-[#686868] mb-8">
            Understanding these definitions helps you better communicate with guests about their dietary needs.
          </p>

          {/* Health Restrictions Section */}
          <div className="mb-8 relative">
            {/* Left colored line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E53935] rounded-l-lg"></div>

            {/* Card container */}
            <div className="ml-1 bg-[#FFF5F5] rounded-tr-xl rounded-br-xl overflow-hidden">
              {/* Header with icon and pill button */}
              <div className="px-4 py-3 flex items-center gap-3">
                {/* Icon in rounded square with white border */}
                <div className="bg-[#E53935] rounded-lg p-2 flex-shrink-0 flex items-center justify-center">
                  <Heart size={18} className="text-white" />
                </div>
                {/* Pill-shaped button with text */}
                <div className="bg-[#E53935] rounded-full px-2 sm:px-4 py-1.5">
                  <h3 className="text-xs sm:text-base font-bold text-white">Health restrictions</h3>
                </div>
              </div>
              {/* Content area */}
              <div className="p-4 sm:p-6 text-left">
                <p className="text-sm sm:text-base font-bold text-[#333333] mb-2">
                  Ingredients that are <strong>INCLUDED</strong> in the dish.
                </p>
                <p className="text-xs sm:text-sm text-[#777777] mb-4">
                  Select items that are present in the dish and may affect health sensitivities.
                </p>

                {/* Options grid */}
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {healthOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleHealthOption(option)}
                      className={`
                        px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all
                        ${healthRestrictions.includes(option)
                          ? 'bg-[#E53935] text-white shadow-md'
                          : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Belief Accommodations Section */}
          <div className="mb-8 relative">
            {/* Left colored line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7B1FA2] rounded-l-lg"></div>

            {/* Card container */}
            <div className="ml-1 bg-[#F5F0F8] rounded-tr-xl rounded-br-xl overflow-hidden">
              {/* Header with icon and pill button */}
              <div className=" px-4 py-3 flex items-center gap-3">
                {/* Icon in rounded square with white border */}
                <div className="bg-[#7B1FA2] rounded-lg p-2 flex-shrink-0 flex items-center justify-center">
                  <div className="flex items-center gap-0.5">
                    <Moon size={16} className="text-white" />
                    <Star size={14} className="text-white" />
                  </div>
                </div>
                {/* Pill-shaped button with text */}
                <div className="bg-[#7B1FA2] rounded-full px-2 sm:px-4 py-1.5">
                  <h3 className="text-xs sm:text-base font-bold text-white">Belief accommodations</h3>
                </div>
              </div>

              {/* Content area */}
              <div className="p-4 sm:p-6 text-left">
                <p className="text-sm sm:text-base font-bold text-[#333333] mb-2">
                  Who this dish <strong>ACCOMMODATES</strong> by belief.
                </p>
                <p className="text-xs sm:text-sm text-[#777777] mb-4">
                  Choose accommodations this dish naturally meets without modification.
                </p>

                {/* Options grid */}
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {beliefOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleBeliefOption(option)}
                      className={`
                        px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all
                        ${beliefAccommodations.includes(option)
                          ? 'bg-[#7B1FA2] text-white shadow-md'
                          : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Lifestyle Accommodations Section */}
          <div className="mb-8 relative">
            {/* Left colored line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#43A047] rounded-l-lg"></div>

            {/* Card container */}
            <div className="ml-1 bg-[#F0F8F1] rounded-tr-xl rounded-br-xl overflow-hidden">
              <div className="px-4 py-3 flex items-center gap-3">
                <div>
                  <div className="bg-[#43A047] rounded-lg p-2 flex-shrink-0 flex items-center justify-center">
                    <Leaf size={18} className="text-white" />
                  </div>
                </div>
                <div>
                  <div className="bg-[#43A047] rounded-full px-2 sm:px-4 py-1.5">
                    <h3 className="text-xs sm:text-base font-bold text-white">Lifestyle accommodations</h3>
                  </div>
                </div>
                {/* Pill-shaped button with text */}
              </div>

              {/* Content area */}
              <div className="p-4 sm:p-6 text-left">
                <p className="text-sm sm:text-base font-bold text-[#333333] mb-2">
                  Who this dish <strong>ACCOMMODATES</strong> by lifestyle.
                </p>
                <p className="text-xs sm:text-sm text-[#777777] mb-4">
                  Pick lifestyles this dish suits as served.
                </p>

                {/* Options grid */}
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {lifestyleOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => toggleLifestyleOption(option)}
                      className={`
                        px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all
                        ${lifestyleAccommodations.includes(option)
                          ? 'bg-[#43A047] text-white shadow-md'
                          : 'bg-gray-200 text-[#4A4A4A] hover:bg-gray-300'
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Key Points Section */}
          <div className="mb-8 bg-blue-100/70 rounded-2xl p-4">
            <h3 className="text-base sm:text-lg text-left font-semibold text-blue-700 mb-4">
              Key points to remember:
            </h3>
            <ul className="list-disc pl-5 space-y-3 text-sm sm:text-base text-start text-blue-700 marker:text-blue-500">
              <li>
                <strong>"Included"</strong> = the ingredient is in the dish.
              </li>
              <li>
                <strong>"Accommodates"</strong> = the dish already fits this need; no changes required.
              </li>
              <li>
                You'll tag what's <strong>IN</strong> the dish (Health) and what it <strong>ACCOMMODATES</strong> (Belief/Lifestyle).
              </li>
            </ul>
          </div>

          {/* Quick Definitions Section */}
          <div className="mb-8 bg-gray-50 rounded-xl">
            <div
              className="w-full flex items-center justify-between text-left rounded-lg px-4 py-3 transition-colors"
            >
              <h3 className="text-base sm:text-lg font-semibold text-[#333333]">
                Quick definitions
              </h3>
            </div>


            <div className="mt-4 space-y-3 py-4 px-4">
              <div className="relative pl-4 bg-[#F5F0F8]  rounded-tr-xl rounded-br-xl py-3 px-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7B1FA2]"></div>
                <div className='text-left flex flex-col'>
                  <span className="font-bold text-sm sm:text-base text-[#333333]">Kosher</span>
                  <span className="text-sm sm:text-base text-[#333333]">
                    Prepared according to Jewish dietary laws—no pork, shellfish, or meat mixed with dairy.
                  </span>
                </div>
              </div>

              <div className="relative pl-4 bg-[#F0F8F1]  rounded-tr-xl rounded-br-xl py-3 px-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#43A047]"></div>
                <div className='text-left flex flex-col'>
                  <span className="font-bold text-sm sm:text-base text-[#333333]">Halal</span>
                  <span className="text-sm sm:text-base text-[#333333]">
                    Permitted under Islamic law—no pork or alcohol; meat prepared following halal guidelines.
                  </span>
                </div>
              </div>

              {/* Vegetarian - Green line with light green background */}
              <div className="relative pl-4 bg-[#F0F8F1]  rounded-tr-xl rounded-br-xl py-3 px-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#43A047]"></div>
                <div className='text-left flex flex-col'>
                  <span className="font-bold text-sm sm:text-base text-[#333333]">Vegetarian</span>
                  <span className="text-sm sm:text-base text-[#333333]">
                    Contains no meat, poultry, or fish—may include dairy or eggs.
                  </span>
                </div>
              </div>

              {/* Vegan - Green line with light green background */}
              <div className="relative pl-4 bg-[#F0F8F1]  rounded-tr-xl rounded-br-xl py-3 px-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#43A047]"></div>
                <div className='text-left flex flex-col'>
                  <span className="font-bold text-sm sm:text-base text-[#333333]">Vegan</span>
                  <span className="text-sm sm:text-base text-[#333333]">
                    Contains no animal products at all, including dairy, eggs, and honey.
                  </span>
                </div>
              </div>

              {/* Pescatarian - Green line with light green background */}
              <div className="relative pl-4 bg-[#F0F8F1] rounded-tr-xl rounded-br-xl py-3 px-4">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#43A047]"></div>
                <div className='text-left flex flex-col'>
                  <span className="font-bold text-sm sm:text-base text-[#333333]">Pescatarian</span>
                  <span className="text-sm sm:text-base text-[#333333]">
                    Excludes meat and poultry but includes fish and seafood.
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Start Button */}
          <div className="flex justify-end mt-8">
            <Button
              onClick={onStart}
              className="bg-[#C62828] hover:bg-[#B71C1C] text-white px-6 py-3 rounded-md font-semibold text-base sm:text-lg shadow-md transition-all"
            >
              Start
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FoodRestrictionsIntro;

